import React, { useEffect, useRef, useState } from "react";
import { FaHome, FaDollarSign, FaRegAddressBook, FaCommentDots, FaBuilding, FaBed, FaSpinner, FaPlusCircle, FaRupeeSign, FaUserCog, FaCalendarAlt, FaTrash, FaPlus } from 'react-icons/fa';
import Dropdown from "../Common/Dropdown/main";
import { Popup } from "../Common/Popup/main";
import { Property } from "../../hook/types/property";
import { DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useProperties } from "../../hook/useProperties";

interface PropertyFormProps {
  initialData: Property;
  onSubmit: (formData: Property) => void;
  onCancel?: () => void;
  isOpen: boolean;
  isLoading: boolean;
  id?: string;
}

interface RentPeriod {
  id?: number;
  rentAmount: string;
  startDate: string;
  endDate: string | null;
}

const PropertyForm: React.FC<PropertyFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isOpen,
  isLoading,
  id
}) => {
  const [formData, setFormData] = useState<Property>({
    ...initialData,
    propertyStatus: initialData?.propertyStatus !== undefined ? initialData.propertyStatus : "UNOCCUPIED",
    isActive: initialData?.isActive ?? true,
  });
  
  const [rentPeriods, setRentPeriods] = useState<RentPeriod[]>([]);
  const [currentRent, setCurrentRent] = useState<RentPeriod>({
    rentAmount: "",
    startDate: "",
    endDate: null
  });
  
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [url1, setUrl1] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const typeDropdownRef = useRef<HTMLDivElement>(null);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  // Get the rental agreement functions from the hook
  const { 
    RentalAgreements, 
    addRentalAgreement, 
    updateRentalAgreement, 
    deleteRentalAgreement 
  } = useProperties();

  // Fetch rental agreements when component mounts or property ID changes
  const { data: fetchedRentalAgreements, isLoading: isLoadingRent } = RentalAgreements(id ? parseInt(id) : 0);

  useEffect(() => {
    setFormData({
      ...initialData,
      propertyStatus: initialData?.propertyStatus !== undefined ? initialData.propertyStatus : "UNOCCUPIED",
      isActive: id ? initialData?.isActive : true,
    });
  }, [initialData, id]);

  // Initialize rent periods from fetched rental agreements
  useEffect(() => {
    if (fetchedRentalAgreements && fetchedRentalAgreements.length > 0) {
      const formattedRentPeriods: RentPeriod[] = fetchedRentalAgreements.map(agreement => ({
        id: agreement.id,
        rentAmount: agreement.rentAmount.toString(),
        startDate: agreement.startDate?.split(' ')[0], // Remove time part if exists
        endDate: agreement.endDate?.split(' ')[0] || null// Remove time part if exists
      }));
      setRentPeriods(formattedRentPeriods);
    } else if ((initialData as any).rentPeriods) {
      // Fallback to initialData if no fetched agreements
      setRentPeriods((initialData as any).rentPeriods);
    } else {
      // Add an empty row by default for new properties
      setRentPeriods([{
        rentAmount: "",
        startDate: "",
        endDate: null
      }]);
    }
  }, [fetchedRentalAgreements, initialData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        statusDropdownRef.current && 
        !statusDropdownRef.current.contains(event.target as Node) &&
        typeDropdownRef.current && 
        !typeDropdownRef.current.contains(event.target as Node)
      ) {
        setStatusDropdownOpen(false);
        setTypeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleStatusDropdownClick = () => {
    setTypeDropdownOpen(false);
    setStatusDropdownOpen(!statusDropdownOpen);
  };

  const handleTypeDropdownClick = () => {
    setStatusDropdownOpen(false);
    setTypeDropdownOpen(!typeDropdownOpen);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value,
    });
  };

  const handleRentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentRent({
      ...currentRent,
      [e.target.id]: e.target.value,
    });
  };

  const handleDateChange = (field: string, date: Dayjs | null) => {
    setCurrentRent({
      ...currentRent,
      [field]: date ? date.format('YYYY-MM-DD') : "",
    });
  };

  const addRentPeriod = async () => {
    if (currentRent.rentAmount && currentRent.startDate  && id) {
      try {
        const agreementData = {
          propertyId: parseInt(id),
          rentAmount: parseInt(currentRent.rentAmount),
          startDate: currentRent.startDate,
          endDate: currentRent.endDate || null
        };
        
        await addRentalAgreement.mutateAsync(agreementData);
        setCurrentRent({
          rentAmount: "",
          startDate: "",
          endDate: null
        });
      } catch (error) {
        console.error("Error adding rent period:", error);
      }
    }
  };

  const addEmptyRentPeriod = () => {
    setRentPeriods([...rentPeriods, {
      rentAmount: "",
      startDate: "",
      endDate: null
    }]);
  };

  // const updateRentPeriod = (index: number, field: string, value: string) => {
  //   const updatedRentPeriods = [...rentPeriods];
  //   updatedRentPeriods[index] = {
  //     ...updatedRentPeriods[index],
  //     [field]: value
  //   };
  //   setRentPeriods(updatedRentPeriods);
  // };

  // const updateRentPeriodDate = (index: number, field: string, date: Dayjs | null) => {
  //   const updatedRentPeriods = [...rentPeriods];
  //   updatedRentPeriods[index] = {
  //     ...updatedRentPeriods[index],
  //     [field]: date ? date.format('YYYY-MM-DD') : ""
  //   };
  //   setRentPeriods(updatedRentPeriods);
  // };

  const updateRentPeriod = (index: number, field: string, value: string) => {
  const updatedRentPeriods = [...rentPeriods];
  updatedRentPeriods[index] = {
    ...updatedRentPeriods[index],
    [field]: value === "" ? null : value  // Convert empty string to null
  };
  setRentPeriods(updatedRentPeriods);
};

const updateRentPeriodDate = (index: number, field: string, date: Dayjs | null) => {
  const updatedRentPeriods = [...rentPeriods];
  updatedRentPeriods[index] = {
    ...updatedRentPeriods[index],
    [field]: date ? date.format('YYYY-MM-DD') : null  // Use null instead of empty string
  };
  setRentPeriods(updatedRentPeriods);
};
  const saveRentPeriod = async (index: number) => {
    const period = rentPeriods[index];
    if (period.rentAmount && period.startDate && id) {
      try {
        if (period.id) {
          // Update existing agreement
          const agreementData = {
            id: period.id,
            propertyId: parseInt(id),
            rentAmount: parseInt(period.rentAmount),
            startDate: period.startDate,
            endDate: period.endDate || null
          };
          
          await updateRentalAgreement.mutateAsync(agreementData);
        } else {
          // Create new agreement
          const agreementData = {
            propertyId: parseInt(id),
            rentAmount: parseInt(period.rentAmount),
            startDate: period.startDate,
            endDate: period.endDate || null
          };
          
          await addRentalAgreement.mutateAsync(agreementData);
        }
      } catch (error) {
        console.error("Error saving rent period:", error);
      }
    }
  };

  const removeRentPeriod = async (index: number) => {
    const period = rentPeriods[index];
    // Don't remove the last empty row
    if (rentPeriods.length <= 1 && !period.rentAmount && !period.startDate && !period.endDate) {
      return;
    }
    
    if (period.id) {
      try {
        await deleteRentalAgreement.mutateAsync(period.id);
        const updatedRentPeriods = [...rentPeriods];
        updatedRentPeriods.splice(index, 1);
        
        // Ensure at least one empty row remains
        if (updatedRentPeriods.length === 0) {
          updatedRentPeriods.push({ rentAmount: "", startDate: "", endDate: null });
        }
        
        setRentPeriods(updatedRentPeriods);
      } catch (error) {
        console.error("Error removing rent period:", error);
      }
    } else {
      const updatedRentPeriods = [...rentPeriods];
      updatedRentPeriods.splice(index, 1);
      
      // Ensure at least one empty row remains
      if (updatedRentPeriods.length === 0) {
        updatedRentPeriods.push({ rentAmount: "", startDate: "", endDate: null });
      }
      
      setRentPeriods(updatedRentPeriods);
    }
  };

  const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStatus = e.target.checked;
    setFormData((prev) => ({ ...prev, isActive: newStatus }));
  };

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    const fields = ["propertyName", 
      "propertyAddress",
      "propertyDepositAmount", 
      "propertyType",
      "propertyStatus"
    ];

    fields.forEach(field => {
      if (!formData[field as keyof Property]) {
        newErrors[field] = `${field.replace(/([A-Z])/g, ' $1')} is required.`;
      }
    });

    if (isNaN(Number(formData.propertyDepositAmount))) {
      newErrors.depositAmount = "Deposit amount must be a number.";
    }

    if (isNaN(Number(formData.propertySize))) {
      newErrors.size = "Size must be a number.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newDocumentUrl = files.length > 0 ? URL.createObjectURL(files[0]) : null;
    setUrl1(newDocumentUrl as any);
    setFormData({
      ...formData,
      propertyImageUrl: files[0] as any
    });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newDocumentUrl1 = files.length > 0 ? URL.createObjectURL(files[0]) : null;
    setUrl1(newDocumentUrl1 as any);
    setFormData({
      ...formData,
      propertyImageUrl: files[0] as any,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      try {
        await onSubmit(formDataRef.current as any);
      } catch (error) {
        console.error("Error submitting form:", error);
      }
    }
  };

  const handleImageClick = (url: string | null) => {
    setImageUrl(url);
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
    setImageUrl(null);
  };

  if (!isOpen) {
    return null;
  }


  const ToggleSwitch: React.FC = () => (
    <div className="flex items-center space-x-2">
      <label htmlFor="active-toggle" className="block text-sm text-gray-700 font-bold flex items-center">
        <FaUserCog className="mr-2 h-5 text-green-500 w-5" /> Status:
      </label>
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          id="active-toggle"
          className="sr-only peer"
          checked={formData.isActive} 
          onChange={handleToggleChange}
          disabled={isLoading}
        />
        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
        <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">
          {formData.isActive ? 'Active' : 'Inactive'}
        </span>
      </label>
    </div>
  );

  return (
    <div className="bg-white rounded-lg max-w-4xl mx-auto p-6">
      <h1 className="text-lg font-bold mb-6 flex justify-between items-center">
        Property Information <span className="text-md italic pr-10">{id ? id : ""}</span>
      </h1>
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div className="md:grid md:grid-cols-2 gap-2 p-1 flex flex-col">
          <div className="flex flex-col gap-1">
            <label htmlFor="propertyName" className="text-sm text-gray-700 font-bold flex items-center">
              <FaHome className="text-blue-500 h-6 w-6 mr-2" /> Property Name
            </label>
            <input
              type="text"
              id="propertyName"
              value={formData?.propertyName || ""}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full text-gray-700 leading-tight focus:outline-none p-3 focus:shadow-outline"
            />
            {errors?.propertyName && <p className="text-red-500 text-xs italic">{errors?.propertyName}</p>}
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="propertyStatus" className="text-sm text-gray-700 font-bold flex items-center">
              <FaBuilding className="text-green-500 h-6 w-6 mr-2" /> Property Status
            </label>
            <div
              ref={statusDropdownRef}
              onClick={handleStatusDropdownClick}>
              <Dropdown
                className2="py-3"
                options={["OCCUPIED", "UNOCCUPIED"]}
                onSelect={(e: any) => {
                  setFormData({ ...formData, propertyStatus: e });
                  setStatusDropdownOpen(false);
                }}
                activeOption={formData?.propertyStatus || "UNOCCUPIED"}
                className=" w-[100%]"
                isOpen={statusDropdownOpen}
                setIsOpen={setStatusDropdownOpen}
                closeOtherDropdown={()=>{}}
              />
            </div>
            {errors?.propertyStatus && <p className="text-red-500 text-xs italic">{errors?.propertyStatus}</p>}
          </div>
          
          <div className="flex flex-col gap-2">
            <label htmlFor="propertyAddress" className="text-sm text-gray-700 font-bold flex items-center">
              <FaRegAddressBook className="text-purple-500 h-6 w-6 mr-2" /> Property Address
            </label>
            <textarea
              id="propertyAddress"
              value={formData?.propertyAddress || ""}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            {errors?.propertyAddress && <p className="text-red-500 text-xs italic">{errors?.propertyAddress}</p>}
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="propertyType" className="text-sm text-gray-700 font-bold flex items-center">
              <FaHome className="text-yellow-500 h-6 w-6 mr-2" /> Property Type
            </label>
            <div 
              ref={typeDropdownRef}
              onClick={handleTypeDropdownClick}>
              <Dropdown
                options={["RESIDENTIAL", "COMMERCIAL"]}
                className2="py-3"
                onSelect={(e: any) => {
                  setFormData({ ...formData, propertyType: e });
                  setTypeDropdownOpen(false);
                }}
                activeOption={formData?.propertyType}
                className=" w-[100%]"
                isOpen={typeDropdownOpen}
                setIsOpen={setTypeDropdownOpen}
                closeOtherDropdown={()=>{}}
              />
            </div>
            {errors?.propertyType && <p className="text-red-500 text-xs italic">{errors?.propertyType}</p>}
          </div>
          
          <div className="flex flex-col gap-2">
            <label htmlFor="noOfRooms" className="text-sm text-gray-700 font-bold flex items-center">
              <FaBed className="text-teal-500 h-6 w-6 mr-2" /> Number of Rooms
            </label>
            <input
              type="text"
              id="noOfRooms"
              value={formData?.noOfRooms || ""}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            {errors?.noOfRooms && <p className="text-red-500 text-xs italic">{errors?.noOfRooms}</p>}
          </div>
          
          <div className="flex flex-col gap-2">
            <label htmlFor="propertyDepositAmount" className="text-sm text-gray-700 font-bold flex items-center">
              <FaRupeeSign className="text-red-600 h-6 w-6 mr-2" /> Deposit Amount
            </label>
            <input
              type="text"
              id="propertyDepositAmount"
              value={formData?.propertyDepositAmount || ""}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            {errors?.propertyDepositAmount && <p className="text-red-500 text-xs italic">{errors?.propertyDepositAmount}</p>}
          </div>
          
          <div className="flex flex-col gap-2">
            <label htmlFor="propertySize" className="text-sm text-gray-700 font-bold flex items-center">
              <FaHome className="text-gray-500 h-6 w-6 mr-2" /> propertySize (in Sq. ft.)
            </label>
            <input
              type="text"
              id="propertySize"
              value={formData?.propertySize || ""}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            {errors?.propertySize && <p className="text-red-500 text-xs italic">{errors?.propertySize}</p>}
          </div>
       
          <div className="mt-4">
            <label
              htmlFor="propertyImageUrl"
              className="text-sm text-gray-700 font-bold flex items-center"
            ><FaPlusCircle className="mr-2 text-red-500 h-4 w-4" /> Upload Document
            </label>
            <input
              type="file"
              id="propertyImageUrl"
              onChange={handleImageChange}
              className="hidden"
            />
            <label
              htmlFor="propertyImageUrl"
              className="cursor-pointer text-blue-500"
            >
              Drag and drop file here or click to upload
            </label>
            {formData?.propertyImageUrl && (
              <p className="text-sm text-gray-700">
                Document uploaded:{" "}
                <span className="cursor-pointer" onClick={() => handleImageClick(url1 ? url1 : formData.propertyImageUrl)}  rel="noopener noreferrer">
                  View
                </span>
              </p>
            )}
          </div>

          {/* Active/Inactive Toggle Button */}
          <div className="flex gap-1 flex-col justify-center">
            <ToggleSwitch />
          </div>

         {/* Monthly Rent Section */}
          <div className="col-span-2 mt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-md font-bold flex items-center">
                <FaDollarSign className="text-green-600 h-5 w-5 mr-2" /> Monthly Rent History
              </h3>
              <button
                type="button"
                onClick={addEmptyRentPeriod}
                className="bg-primary hover:bg-secondary text-white font-bold py-2 px-3 rounded flex items-center text-sm"
                disabled={addRentalAgreement.isPending}
              >
                <FaPlus className="mr-1" /> Add Rent Period
              </button>
            </div>
            
        
            
            {/* Rent periods list - Always show at least one row */}
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="py-2 px-4 border-b text-left">Rent Amount</th>
                    <th className="py-2 px-4 border-b text-left">Start Date</th>
                    <th className="py-2 px-4 border-b text-left">End Date</th>
                    <th className="py-2 px-4 border-b text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rentPeriods.map((period, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-2 px-4">
                        <input
                          type="number"
                          value={period.rentAmount}
                          onChange={(e) => updateRentPeriod(index, 'rentAmount', e.target.value)}
                          className="shadow appearance-none border rounded w-full py-1 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          placeholder="Amount"
                        />
                      </td>
                      <td className="py-2 px-4">
                        <DatePicker
                          className="w-full"
                          placeholder="Start date"
                          value={period.startDate ? dayjs(period.startDate) : null}
                          onChange={(date) => updateRentPeriodDate(index, 'startDate', date)}
                        />
                      </td>
                      <td className="py-2 px-4">
                        <DatePicker
                          className="w-full"
                          placeholder="End date"
                          value={period.endDate ? dayjs(period.endDate) : null}
                          onChange={(date) => updateRentPeriodDate(index, 'endDate', date)}
                        />
                      </td>
                      <td className="py-2 px-4 flex space-x-2">
                        <button
                          type="button"
                          onClick={() => saveRentPeriod(index)}
                          className="bg-green-500 hover:bg-green-600 text-white font-bold py-1 px-2 rounded text-sm"
                          disabled={addRentalAgreement.isPending || updateRentalAgreement.isPending || !period.rentAmount || !period.startDate}
                        >
                          {addRentalAgreement.isPending || updateRentalAgreement.isPending ? <FaSpinner className="animate-spin" /> : "Save"}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeRentPeriod(index)}
                          className="bg-red-500 hover:bg-red-600 text-white font-bold py-1 px-2 rounded text-sm"
                          disabled={deleteRentalAgreement.isPending || (rentPeriods.length <= 1 && !period.rentAmount && !period.startDate)}
                        >
                          {deleteRentalAgreement.isPending ? <FaSpinner className="animate-spin" /> : <FaTrash />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row justify-end mt-4 space-y-2 sm:space-y-0 sm:space-x-4">
          <button
            disabled={isLoading}
            type="submit"
            className="bg-primary hover:bg-secondary text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            {isLoading ? <p className="flex justify-center items-center"><FaSpinner className="animate-spin mr-2"/> Updating...</p> : "Save"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="bg-red-400 hover:bg-red-500 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Cancel
          </button>
        </div>
      </form>
      
      {showPopup && (
        <Popup
          className="p-4 md:p-6 lg:p-10 max-w-sm md:max-w-md lg:max-w-lg w-full max-h-[90vh] overflow-y-auto"
          closeButton={true}
          onClose={closePopup}
        >
          <div className="w-full h-auto">
            <img
              src={imageUrl || ''}
              alt="Uploaded"
              className="w-full h-auto max-h-[80vh] object-contain rounded-xl"
            />
          </div>
        </Popup>
      )}
    </div>
  );
};

export default PropertyForm;